import { PropsWithChildren, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { has } from 'lodash'

import { Heap, useHeapDispatch } from 'context/Heap'
import { useFlow, useFlowSlug } from 'utils/flow'
import { useLocalStorage, useSearchParamsObj } from 'utils/hook'

import PersistWatch from './PersistWatch'

type PrimitiveFormValue = string | boolean
type FormObject = Record<string, PrimitiveFormValue | number>

interface FormValue {
  [key: string]: PrimitiveFormValue | FormObject | FormObject[]
}

function FormWrapper({ children }: PropsWithChildren): JSX.Element {
  const flowSlug = useFlowSlug()
  const { data: flow } = useFlow()
  const { flow_session: flowSession } = flow!
  const urlParams = useSearchParamsObj()

  const [heapDispatch] = useHeapDispatch()

  const [values] = useLocalStorage(`${flowSlug}/form`, {})

  const [metaDataValue, setMetaData] = useLocalStorage(`${flowSlug}/metadata`, {})

  const defaultValues: FormValue = useMemo(
    () => ({
      ...values,
      ...(has(flowSession, 'flow') && flowSession.full_data),
      ...(has(urlParams, 'fields') && urlParams.fields),
    }),
    []
  )

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues,
  })

  useEffect(() => {
    if (has(urlParams, 'heap')) {
      heapDispatch({ type: 'add', fields: urlParams.heap as Heap })
    }

    if (has(urlParams, 'meta')) {
      setMetaData({
        ...metaDataValue,
        ...urlParams.meta,
      })
    }

    if (has(urlParams, 's_id')) {
      setMetaData({
        ...metaDataValue,
        ...flowSession.metadata,
      })

      heapDispatch({ type: 'add', fields: flowSession.heap })
    }
  }, [])

  return (
    <FormProvider {...methods}>
      <PersistWatch />
      {children}
    </FormProvider>
  )
}

export default FormWrapper
